<template>
  <v-container fluid class="pa-4 pa-md-6">
    <!-- Header -->
    <v-row class="mb-4">
      <v-col cols="12">
        <div>
          <h1 class="dashboard-title">Rentabilidad Comparativa</h1>
          <p class="text-caption grey--text">M&aacute;rgenes, GMROI y tendencia de rentabilidad</p>
        </div>
        <PeriodSelector :loading="loading" default-preset="30d" @change="onPeriodChange" class="mt-2" />
      </v-col>
    </v-row>

    <!-- KPI Cards with Deltas -->
    <v-row class="mb-4">
      <v-col cols="6" sm="6" lg="3">
        <KpiDeltaCard label="Utilidad Bruta" :value="data.utilidad" :previous-value="prevUtilidad" format="currency" icon="mdi-cash-multiple" gradient="success-gradient" />
      </v-col>
      <v-col cols="6" sm="6" lg="3">
        <KpiDeltaCard label="Margen Bruto" :value="data.margen_pct" :previous-value="prevMargen" format="percent" icon="mdi-percent" gradient="primary-gradient" />
      </v-col>
      <v-col cols="6" sm="6" lg="3">
        <KpiDeltaCard label="GMROI" :value="data.gmroi_global" format="decimal" icon="mdi-chart-areaspline" gradient="warning-gradient" subtitle="Retorno por $ invertido" />
      </v-col>
      <v-col cols="6" sm="6" lg="3">
        <KpiDeltaCard label="Sell-Through" :value="data.sell_through" format="percent" icon="mdi-swap-horizontal" gradient="info-gradient" subtitle="Tasa de venta" />
      </v-col>
    </v-row>

    <!-- Margin Trend 6 months -->
    <v-row class="mb-4">
      <v-col cols="12" md="6">
        <v-card class="elevation-0" outlined>
          <v-card-title class="section-title pa-4 pb-2">
            <v-icon class="mr-2" color="primary">mdi-chart-line</v-icon>
            Tendencia de Margen (6 meses)
          </v-card-title>
          <v-card-text>
            <GChart
              v-if="marginTrendChart.length > 1"
              type="LineChart"
              :data="marginTrendChart"
              :options="trendChartOptions"
              class="chart-container"
            />
            <div v-else class="text-center grey--text pa-6">Sin datos</div>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Margen por Categoría Dual Bar -->
      <v-col cols="12" md="6">
        <v-card class="elevation-0" outlined>
          <v-card-title class="section-title pa-4 pb-2">
            <v-icon class="mr-2" color="success">mdi-chart-bar</v-icon>
            Margen % por Categor&iacute;a
            <v-spacer></v-spacer>
            <span class="text-caption grey--text">Verde: actual &bull; Gris: anterior</span>
          </v-card-title>
          <v-card-text>
            <GChart
              v-if="marginDualBarChart.length > 1"
              type="ColumnChart"
              :data="marginDualBarChart"
              :options="dualBarOptions"
              class="chart-container"
            />
            <div v-else class="text-center grey--text pa-6">Sin datos</div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Top / Bottom Products -->
    <v-row class="mb-4">
      <v-col cols="12" md="6">
        <v-card class="elevation-0" outlined>
          <v-card-title class="section-title pa-4 pb-2">
            <v-icon class="mr-2" color="success">mdi-arrow-up-bold</v-icon>
            Top 20 M&aacute;s Rentables
          </v-card-title>
          <v-data-table
            v-if="!isMobile"
            :headers="profitHeaders"
            :items="data.top_profit"
            :loading="loading"
            dense
            :items-per-page="10"
            class="analyst-table"
            no-data-text="Sin datos"
          >
            <template v-slot:item.ventas="{ item }">{{ formatCurrency(item.ventas) }}</template>
            <template v-slot:item.utilidad="{ item }">{{ formatCurrency(item.utilidad) }}</template>
            <template v-slot:item.margen_pct="{ item }">
              <v-chip :color="item.margen_pct > 30 ? 'success' : item.margen_pct > 15 ? 'warning' : 'error'" text-color="white" x-small>
                {{ item.margen_pct }}%
              </v-chip>
            </template>
          </v-data-table>
          <v-list v-else dense class="pa-0">
            <div v-if="loading" class="text-center pa-4"><v-progress-circular indeterminate size="24"></v-progress-circular></div>
            <div v-else-if="!data.top_profit.length" class="text-center grey--text pa-4">Sin datos</div>
            <template v-else>
              <v-list-item v-for="(item, i) in data.top_profit.slice(0, 10)" :key="i" class="mobile-item">
                <v-list-item-content>
                  <v-list-item-title class="text-body-2 font-weight-medium">{{ item.marca }} - {{ item.refe }}</v-list-item-title>
                  <v-list-item-subtitle>{{ item.qty }} und &bull; Venta: {{ formatCurrency(item.ventas) }}</v-list-item-subtitle>
                </v-list-item-content>
                <v-list-item-action class="text-right ma-0">
                  <div class="text-body-2 font-weight-bold success--text">{{ formatCurrency(item.utilidad) }}</div>
                  <v-chip :color="item.margen_pct > 30 ? 'success' : item.margen_pct > 15 ? 'warning' : 'error'" text-color="white" x-small>{{ item.margen_pct }}%</v-chip>
                </v-list-item-action>
              </v-list-item>
            </template>
          </v-list>
        </v-card>
      </v-col>

      <v-col cols="12" md="6">
        <v-card class="elevation-0" outlined>
          <v-card-title class="section-title pa-4 pb-2">
            <v-icon class="mr-2" color="error">mdi-arrow-down-bold</v-icon>
            Bottom 20 Menos Rentables
          </v-card-title>
          <v-data-table
            v-if="!isMobile"
            :headers="profitHeaders"
            :items="data.bottom_profit"
            :loading="loading"
            dense
            :items-per-page="10"
            class="analyst-table"
            no-data-text="Sin datos"
          >
            <template v-slot:item.ventas="{ item }">{{ formatCurrency(item.ventas) }}</template>
            <template v-slot:item.utilidad="{ item }">
              <span :class="parseFloat(item.utilidad) < 0 ? 'error--text' : ''">{{ formatCurrency(item.utilidad) }}</span>
            </template>
            <template v-slot:item.margen_pct="{ item }">
              <v-chip :color="item.margen_pct < 0 ? 'error' : item.margen_pct < 10 ? 'warning' : 'success'" text-color="white" x-small>{{ item.margen_pct }}%</v-chip>
            </template>
          </v-data-table>
          <v-list v-else dense class="pa-0">
            <div v-if="loading" class="text-center pa-4"><v-progress-circular indeterminate size="24"></v-progress-circular></div>
            <div v-else-if="!data.bottom_profit.length" class="text-center grey--text pa-4">Sin datos</div>
            <template v-else>
              <v-list-item v-for="(item, i) in data.bottom_profit.slice(0, 10)" :key="i" class="mobile-item">
                <v-list-item-content>
                  <v-list-item-title class="text-body-2 font-weight-medium">{{ item.marca }} - {{ item.refe }}</v-list-item-title>
                  <v-list-item-subtitle>{{ item.qty }} und &bull; Venta: {{ formatCurrency(item.ventas) }}</v-list-item-subtitle>
                </v-list-item-content>
                <v-list-item-action class="text-right ma-0">
                  <div class="text-body-2 font-weight-bold" :class="parseFloat(item.utilidad) < 0 ? 'error--text' : ''">{{ formatCurrency(item.utilidad) }}</div>
                  <v-chip :color="item.margen_pct < 0 ? 'error' : item.margen_pct < 10 ? 'warning' : 'success'" text-color="white" x-small>{{ item.margen_pct }}%</v-chip>
                </v-list-item-action>
              </v-list-item>
            </template>
          </v-list>
        </v-card>
      </v-col>
    </v-row>

    <!-- GMROI por Marca -->
    <v-row>
      <v-col cols="12">
        <v-card class="elevation-0" outlined>
          <v-card-title class="section-title pa-4 pb-2">
            <v-icon class="mr-2" color="purple">mdi-chart-box-outline</v-icon>
            GMROI por Marca
          </v-card-title>
          <v-data-table
            v-if="!isMobile"
            :headers="gmroiHeaders"
            :items="data.gmroi_by_brand"
            :loading="loading"
            dense
            :items-per-page="15"
            class="analyst-table"
            no-data-text="Sin datos"
          >
            <template v-slot:item.utilidad="{ item }">{{ formatCurrency(item.utilidad) }}</template>
            <template v-slot:item.inv_costo="{ item }">{{ formatCurrency(item.inv_costo) }}</template>
            <template v-slot:item.gmroi="{ item }">
              <v-chip :color="item.gmroi > 1 ? 'success' : item.gmroi > 0.5 ? 'warning' : 'error'" text-color="white" x-small>{{ item.gmroi }}</v-chip>
            </template>
            <template v-slot:item.sell_through="{ item }">{{ item.sell_through || 0 }}%</template>
            <template v-slot:item.turnover="{ item }">{{ item.turnover || 0 }}</template>
          </v-data-table>
          <v-list v-else dense class="pa-0">
            <div v-if="loading" class="text-center pa-4"><v-progress-circular indeterminate size="24"></v-progress-circular></div>
            <div v-else-if="!data.gmroi_by_brand.length" class="text-center grey--text pa-4">Sin datos</div>
            <template v-else>
              <v-list-item v-for="(item, i) in data.gmroi_by_brand.slice(0, 15)" :key="i" class="mobile-item">
                <v-list-item-content>
                  <v-list-item-title class="text-body-2 font-weight-medium">{{ item.marca }}</v-list-item-title>
                  <v-list-item-subtitle>Util: {{ formatCurrency(item.utilidad) }} &bull; ST: {{ item.sell_through || 0 }}% &bull; Rot: {{ item.turnover || 0 }}</v-list-item-subtitle>
                </v-list-item-content>
                <v-list-item-action class="ma-0">
                  <v-chip :color="item.gmroi > 1 ? 'success' : item.gmroi > 0.5 ? 'warning' : 'error'" text-color="white" x-small>{{ item.gmroi }}</v-chip>
                </v-list-item-action>
              </v-list-item>
            </template>
          </v-list>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { webserver, formatCurrency } from "@/services/webserver.js";
import { GChart } from "vue-google-charts";
import PeriodSelector from "@/components/PeriodSelector.vue";
import KpiDeltaCard from "@/components/KpiDeltaCard.vue";

export default {
  name: "ProfitAnalysis",
  components: { GChart, PeriodSelector, KpiDeltaCard },
  data() {
    return {
      loading: false,
      period: {},
      data: {
        ventas: 0, costo: 0, utilidad: 0, margen_pct: 0,
        gmroi_global: 0, sell_through: 0,
        prev: null,
        margin_by_category: [],
        margin_trend: [],
        top_profit: [], bottom_profit: [], gmroi_by_brand: [],
      },
      profitHeaders: [
        { text: "Marca", value: "marca", sortable: false },
        { text: "Ref", value: "refe", sortable: false },
        { text: "Cant.", value: "qty", align: "end" },
        { text: "Ventas", value: "ventas", align: "end" },
        { text: "Utilidad", value: "utilidad", align: "end" },
        { text: "Margen", value: "margen_pct", align: "center" },
      ],
      gmroiHeaders: [
        { text: "Marca", value: "marca", sortable: false },
        { text: "Utilidad", value: "utilidad", align: "end" },
        { text: "Inv. Costo", value: "inv_costo", align: "end" },
        { text: "GMROI", value: "gmroi", align: "center" },
        { text: "Sell-Through", value: "sell_through", align: "center" },
        { text: "Rotaci\u00f3n", value: "turnover", align: "center" },
      ],
      trendChartOptions: {
        legend: { position: "none" },
        curveType: "function",
        colors: ["#1a73e8"],
        chartArea: { width: "80%", height: "70%" },
        hAxis: { textStyle: { fontSize: 10 } },
        vAxis: { format: "#'%'", title: "Margen %" },
        height: 280,
      },
      dualBarOptions: {
        legend: { position: "top" },
        colors: ["#34a853", "#dadce0"],
        chartArea: { width: "80%", height: "65%" },
        hAxis: { textStyle: { fontSize: 10 }, slantedText: true },
        vAxis: { format: "#'%'" },
        height: 280,
      },
    };
  },
  computed: {
    isMobile() { return this.$vuetify.breakpoint.xs; },
    prevUtilidad() {
      return this.data.prev ? this.data.prev.utilidad : null;
    },
    prevMargen() {
      return this.data.prev ? this.data.prev.margen_pct : null;
    },
    marginTrendChart() {
      const d = this.data.margin_trend || [];
      if (!d.length) return [["Mes", "Margen %"]];
      return [["Mes", "Margen %"], ...d.map((r) => [r.mes, parseFloat(r.margen_pct)])];
    },
    marginDualBarChart() {
      const rows = this.data.margin_by_category || [];
      if (!rows.length) return [["Categor\u00eda", "Actual", "Anterior"]];
      return [
        ["Categor\u00eda", "Actual", "Anterior"],
        ...rows.map((c) => [
          c.categoria,
          parseFloat(c.margen_pct || 0),
          parseFloat(c.margen_pct_prev || 0),
        ]),
      ];
    },
  },
  methods: {
    formatCurrency,
    onPeriodChange(period) {
      this.period = period;
      this.loadData();
    },
    loadData() {
      if (!this.period.dateFrom) return;
      this.loading = true;
      const store = this.$store.state.profile.store_id || window.store?.store_id;
      webserver("get_profit_analysis", {
        store,
        date_from: this.period.dateFrom,
        date_to: this.period.dateTo,
        compare_from: this.period.compareDateFrom,
        compare_to: this.period.compareDateTo,
      }, (res) => {
        this.loading = false;
        if (res && !res.error) {
          this.data = { ...this.data, ...res };
        }
      });
    },
  },
};
</script>

<style scoped>
.dashboard-title { font-size: 1.5rem; font-weight: 700; color: #424242; }
.section-title { font-size: 0.9375rem; font-weight: 600; color: #424242; }
.analyst-table { font-size: 0.8125rem; }
.chart-container { width: 100%; min-height: 260px; }
.mobile-item { min-height: 48px; border-bottom: 1px solid #f0f0f0; }
.mobile-item:last-child { border-bottom: none; }
</style>
